import React, { useEffect, useRef, useState } from 'react';

import { Tooltip } from '@cimpress/react-components';
import LayerInformation from './information';
import { BACKGROUND, type LayerType } from './layerTypes';
import UploadIcon from 'src/components/UploadIcon';
import { downloadImageToFileSystem } from 'src/util/upload';
import { ENTER } from 'src/util/keycodes';

type LayerProps = {
    id: string;
    type: LayerType;
    warpType: string;
    isProtected?: boolean;
    selected: boolean;
    hidden?: boolean;
    title: string;
    width?: number;
    height?: number;
    className?: string;
    onClick?: (id: string, type: LayerType) => void;
    onDelete?: (id: string) => void;
    onReplace?: (e: React.ChangeEvent<HTMLInputElement>, id: string) => void;
    changeBackgroundName?: (args: { id: string; name: string }) => void;
    toggleHide?: (id: string, type: LayerType) => void;
    hasDragDrop?: boolean;
    onDuplicate?: (id: string) => void;
    onProtected?: () => void;
    onBgRemove?: (id: string) => void;
    url?: string;
};

const onClickButton = (e: React.MouseEvent, func?: () => void) => {
    func && func();
    e.stopPropagation();
};

const Layer: React.FC<LayerProps> = (props) => {
    const {
        onDelete,
        onReplace,
        toggleHide,
        title,
        className,
        selected,
        hidden,
        id,
        type,
        hasDragDrop,
        onDuplicate,
        isProtected,
        onBgRemove,
        url,
        warpType,
        onClick,
        changeBackgroundName,
        onProtected,
        width,
        height,
    } = props;

    const inputRef = useRef<HTMLInputElement>(null);
    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        if (editMode) {
            inputRef.current?.focus();
        }
    }, [editMode]);

    const click = () => {
        onClick && onClick(id, type);
    };

    const replace = (e: React.ChangeEvent<HTMLInputElement>) => {
        onReplace?.(e, id);
    };

    const hide = () => {
        toggleHide?.(id, type);
    };

    const copy = () => {
        onDuplicate?.(id);
        if (selected && onClick) {
            onClick(id, type);
        }
    };

    const toggleEdit = () => {
        if (isProtected) {
            onProtected?.();
            return;
        }

        setEditMode((prev) => !prev);
    };

    const changeName = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        if (evt.keyCode === ENTER) {
            const name = (evt.target as HTMLInputElement).value;
            setEditMode(false);
            changeBackgroundName?.({ id, name });
        }
    };

    const getTitleText = () => {
        let titleText = title;
        if (height && width) {
            titleText += ` (${width} x ${height})`;
        }
        return titleText;
    };

    const dndIcon = <img className='iconLg' src='https://static.ux.cimpress.io/assets/icons/drag-drop-l.svg' />;

    return (
        <div
            className={`layer ${selected ? 'layer--selected' : ''} ${className || ''} ${isProtected ? 'protected' : ''}`}>
            <div className='layer__header' onClick={click} onDoubleClick={toggleEdit}>
                {hasDragDrop && !editMode && dndIcon}
                <p className='layer__title'>
                    {!editMode && getTitleText()}
                    {editMode && (
                        <input
                            ref={inputRef}
                            className='layer__title__input'
                            type='text'
                            onBlur={toggleEdit}
                            onKeyDown={changeName}
                            defaultValue={title}
                        />
                    )}
                </p>

                <div className='layer__actions'>
                    {url && (
                        <Tooltip contents='Download image'>
                            <button
                                className='layer__download'
                                type='button'
                                onClick={(e) =>
                                    onClickButton(e, () => {
                                        downloadImageToFileSystem(url, title);
                                    })
                                }>
                                <img
                                    className='iconLg'
                                    src='https://static.ux.cimpress.io/assets/icons/download-3-l.svg'
                                />
                            </button>
                        </Tooltip>
                    )}
                    {onBgRemove && (
                        <Tooltip contents='Background Removal'>
                            <button
                                className='layer__bg-remove'
                                type='button'
                                onClick={(e) => onClickButton(e, () => onBgRemove(id))}>
                                <img className='iconLg' src='/images/bg-remove-icon.svg' />
                            </button>
                        </Tooltip>
                    )}
                    {!!toggleHide && (
                        <button className='layer__hide' onClick={(e) => onClickButton(e, hide)}>
                            <img
                                className={`iconLg ${hidden ? '' : 'colorOceanBase'}`}
                                src='https://static.ux.cimpress.io/assets/icons/view-2-l.svg'
                            />
                        </button>
                    )}
                    {!isProtected && !!onReplace && (
                        <UploadIcon className='layer__replace' onUpload={replace}>
                            <img
                                className='iconLg'
                                src='https://static.ux.cimpress.io/assets/icons/file-picture-edit-l.svg'
                            />
                        </UploadIcon>
                    )}
                    {!!onDuplicate && (
                        <button className='layer__duplicate' onClick={(e) => onClickButton(e, copy)}>
                            <img className='iconLg' src='https://static.ux.cimpress.io/assets/icons/copy-3-l.svg' />
                        </button>
                    )}
                    {!isProtected && !!onDelete && (
                        <button className='layer__delete' onClick={(e) => onClickButton(e, () => onDelete(id))}>
                            <img className='iconLg' src='https://static.ux.cimpress.io/assets/icons/bin-1-l.svg' />
                        </button>
                    )}
                    {isProtected && (
                        <Tooltip contents='Protected Asset'>
                            <div className='protected-icon' />
                        </Tooltip>
                    )}
                </div>
            </div>
            {selected && type !== BACKGROUND && (
                <div className='layer__body'>
                    <LayerInformation id={id} type={type} warpType={warpType} />
                </div>
            )}
        </div>
    );
};

export default React.memo(Layer);
